//MyComponent.tsx
import React, { ReactNode, useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { Nav, Form, Button, Card, Collapse, Tabs, Tab, Container, ButtonGroup, ToggleButton,
         FloatingLabel, Alert, Toast, ToastContainer, Row, Col, Navbar, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import {
  faListCheck, faNetworkWired, faBookOpenReader, faGauge, faEye, faRoute, faMicrochip,
  faSitemap, faDatabase, faCogs, faExchangeAlt, faChevronLeft, faChevronRight, faListAlt,
  faProjectDiagram, faServer, faChartBar, faChartLine, faBoxes, faTable, faBalanceScale, faRobot
} from '@fortawesome/free-solid-svg-icons';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import Switch from 'react-switch';

import {
  MAX_DATA_DENCITY_THRESOLD_PCT, NULL_DENCITY_THRESOLD_PCT,
  textFieldIconData, integerFieldIconData, dropdownFieldIconData,
  decimalFieldIconData, dateFieldIconData, dateTimeFieldIconData, doubleFieldIconData, booleanFieldIconData,
  isFieldSupposedToBeRemoved, downloadJsonFile, findField, mergeDMJSON, cleanDm, convertToRawDm,
  attachInsightsToDM, attachProfileToDM
} from './constants';

import DataModelTreeModal from "./DataModelTreeModal";
import DataModelTree from "./DataModelTreeLazy";
import SourceSystems from "./SourceSystems";
import LoginModal from "./LoginModal";
import InfaMDMMigrationModal from "./InfaMDMMigrationModal";
import ReltioMDMMigrationModal from "./ReltioMDMMigrationModal";
import DMERDiagram from "./DMERDiagram";
import EntityHierRelationship from "./EntityHierRelationship";
import DataQualityDashboard from "./ddm/DataQualityDashboard";
import MDMSourceManager from './srcSystems/MDMSourceManager';
import LookupEntityWorkspace from "./lkp/LookupEntityWorkspace";
import SdhReviewWorkspace from "./dq/SdhReviewWorkspace";
import DqRulesV2 from "./dq_v2/DqRulesV2";

import {
  adriva_postAction, adriva_getAppList, adriva_getRawMetadata, adriva_convertToRefinedDM,
  adriva_uploadInfaConfigFiles, adriva_getEntityRelationships,
  idmc_login, idmc_getProjectList, idmc_getFolderList, idmc_getSourceRank, idmc_migrate,
  reltio_login, get_reltio_migrate, reltio_migrate,
} from './API';
import { getSources, getEntities, AppEntity, SourceSystem } from './API';

import AriaAssistant from "./assistant/AriaAssistant";
import AriaAssistantModal from "./assistant/AriaAssistantModal";
import LookupReview from "./ddm/LookupReview";
import DataGovernanceCatalog from "./dg/DataGovernanceCatalog";
import Sidebar from './common/Sidebar';
import ProcessUsage from "./common/ProcessUsage";
import GenWizardLanding from "./Landing";

import logo from './img/adriva2.png';
import logoW from './img/adrivaW.png';
import JobPage from "./job/JobPage";
import DataInputPage from "./dataInput/DataInputPage";
import DataLineageCrosswalkView from "./srcSystems/DataLineageCrosswalkLazy";
//import DataLineageCrosswalkView from "./canonical/DataLineageCrosswalkLazy";
import DQDashboardContainer from "./ddm/DQDashboardContainer";
import DQAssessmentReport from "./dq/DQAssessmentReport";

import { transformGovernanceJSON } from "./dg/transformGovernanceJSON";
import ConfigSelectorPage from "./ConfigSelectorPage";
import AppHeader from './common/AppHeader';
import AppSidebar, { SidebarItem } from './common/AppSidebar';
import { injectTheme, T } from './theme/theme';
import MigrateDropdown from "./common/MigrateDropdown";
import { Tool } from "./dataInput/types";
import CanonicalReferenceData from "./canonical/CanonicalReferenceData";

import GovernanceArtifacts from "./dg/GovernanceArtifacts";
import BusinessGlossaryPage, { type NavigateToFieldPayload } from "./dg/BusinessGlossary";
import SourceDashboard from "./dg/DGArtifactsSourceDashboard";
import LookupWorkbench from "./lkp/LookupWorkbench";
import BcReview from "./bc/BcReview";
import EkdReview from "./ekd/EkdReview";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Field {
  id?: string;
  name: string;
  label?: string;
  type?: string;
  [key: string]: any;
}

interface Entity {
  id: string;
  name: string;
  label?: string;
  field?: Field[];
  [key: string]: any;
}

type SystemRank = {
  rnk: string;
  srcCd: string;
  srcId: string;
  flds: string[];
};

// ─── CustomAlert (unchanged) ──────────────────────────────────────────────────

interface CustomAlertProps {
  show: boolean; message: string; error: string;
  bgBlock: boolean; alertWClose: boolean; onClose: () => void;
}

const CustomAlert: React.FC<CustomAlertProps> = ({ show, message, error, bgBlock, onClose, alertWClose }) => {
  const bg = message ? 'success' : (error ? 'danger' : '');
  if (bgBlock && alertWClose) {
    return (
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 105000, marginTop: '90px' }}>
        <Toast show={show} bg={bg} delay={1000}>
          <div className="position-relative">
            <button className="btn-close btn-close-white position-absolute top-0 end-0 m-1" onClick={onClose}></button>
            <Toast.Body className='text-white'>{message || error}</Toast.Body>
          </div>
        </Toast>
      </ToastContainer>
    );
  }
  if (bgBlock && !alertWClose) {
    return (
      <ToastContainer position="top-center" className="p-3" style={{ zIndex: 105000, marginTop: '90px' }}>
        <Toast show={show} bg={bg} onClose={onClose} delay={1000}>
          <Toast.Body className='text-white'>{message || error}</Toast.Body>
        </Toast>
      </ToastContainer>
    );
  }
  return (
    <ToastContainer position="top-center" className="p-3" style={{ zIndex: 105000, marginTop: '90px' }}>
      <Toast show={show} bg={bg} onClose={onClose} delay={4000} autohide>
        <Toast.Body className='text-white'>{message || error}</Toast.Body>
      </Toast>
    </ToastContainer>
  );
};

// ─── EntityNavBar (unchanged) ─────────────────────────────────────────────────

const MAX_ENTITY_NAV_TABS = 9;

interface EntityNavBarProps {
  entities: { id: string; label: string }[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const EntityNavBar: React.FC<EntityNavBarProps> = ({ entities, selectedId, onSelect }) => {
  const [overOpen, setOverOpen] = React.useState(false);
  const overRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const h = (e: MouseEvent) => {
      if (overRef.current && !overRef.current.contains(e.target as Node)) setOverOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const visible  = entities.slice(0, MAX_ENTITY_NAV_TABS);
  const overflow = entities.slice(MAX_ENTITY_NAV_TABS);
  const overflowHasActive = overflow.some(e => e.id === selectedId);

  const tabStyle = (active: boolean): React.CSSProperties => ({
    all: 'unset', boxSizing: 'border-box' as const, display: 'inline-flex', alignItems: 'center',
    padding: '0 14px', height: '100%', fontSize: 12, fontWeight: active ? 600 : 500,
    cursor: 'pointer', whiteSpace: 'nowrap' as const, color: active ? '#1e3a6e' : '#445566',
    borderBottom: active ? '3px solid #319795' : '3px solid transparent',
    background: active ? '#f0f8f8' : 'transparent', transition: 'all .14s',
  });

  return (
    <div style={{ display: 'flex', alignItems: 'stretch', background: '#fff',
      borderBottom: '2px solid #c8d4e0', height: 44, flexShrink: 0, overflow: 'visible', padding: '0 4px' }}>
      {visible.map(e => (
        <button key={e.id} style={tabStyle(e.id === selectedId)} onClick={() => onSelect(e.id)} title={e.label}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 160 }}>
            {e.label}
          </span>
        </button>
      ))}
      {overflow.length > 0 && (
        <div ref={overRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <button style={{ ...tabStyle(overflowHasActive), gap: 5, padding: '0 12px' }}
            onClick={() => setOverOpen(o => !o)}>
            {overflowHasActive
              ? <span style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {overflow.find(e => e.id === selectedId)?.label ?? 'More'}
                </span>
              : 'More'}
            <span style={{ fontSize: 9, background: '#dde7f5', color: '#1e3a6e',
              padding: '1px 5px', borderRadius: 99, fontWeight: 700 }}>{overflow.length}</span>
            <span style={{ fontSize: 8 }}>▾</span>
          </button>
          {overOpen && (
            <div style={{ position: 'absolute', top: 'calc(100% + 3px)', left: 0, zIndex: 500,
              background: '#fff', border: '1px solid #c8d4e0', borderRadius: 9,
              boxShadow: '0 10px 30px rgba(0,0,0,.12)', minWidth: 180, overflow: 'hidden' }}>
              {overflow.map(e => (
                <div key={e.id} onClick={() => { onSelect(e.id); setOverOpen(false); }}
                  style={{ padding: '9px 14px', fontSize: 12.5, cursor: 'pointer',
                    color: e.id === selectedId ? '#1e3a6e' : '#1e2d3d',
                    background: e.id === selectedId ? '#dde7f5' : 'transparent',
                    fontWeight: e.id === selectedId ? 600 : 400, transition: 'background .1s' }}>
                  {e.label}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MyComponent — converted to functional component
// ─────────────────────────────────────────────────────────────────────────────

export default function MyComponent() {

  // ── theme ──────────────────────────────────────────────────────────────────
  useEffect(() => { injectTheme(); }, []);

  // ── core state ─────────────────────────────────────────────────────────────
  const [pageName,            setPageName]            = useState('INITIAL_PAGE');
  const [sideBarCollapsed,    setSideBarCollapsed]    = useState(false);
  const [tgtMDM,              setTgtMDM]              = useState('');
  const [tool,                setToolState]           = useState('');
  const [appNm,               setAppNm]               = useState('');
  const [allAppList,          setAllAppList]           = useState<any[]>([]);
  const [toolAppList,         setToolAppList]         = useState<any[]>([]);
  const [tabKey,              setTabKey]              = useState('businessEntity');
  const pendingTabRef = useRef<string | null>(null); // explicit tab a navigation wants to keep through a context change
  // `data` holds entity entries from build/save flows only. The lazy DM views
  // (DataModelTree / DMTreeGridSummary) self-fetch paginated data via getDMProfile
  // and do NOT populate this.
  const [data,                setData]                = useState<Entity[]>([]);
  // Lean, lazily-fetched source + entity lists (replace deriving from selectedApp.setup).
  // MyComponent owns these and feeds the top entity bar of every secondary page,
  // so individual pages don't each have to fetch the entity list themselves.
  const [sourceList,          setSourceList]          = useState<SourceSystem[]>([]);
  const [entityList,          setEntityList]          = useState<AppEntity[]>([]);
  const [selectedEntityId,    setSelectedEntityId]    = useState<string>('');   // entityId (stable key)
  const [origData,            setOrigData]            = useState<Entity[]>([]);
  const [srcSystems,          setSrcSystems]          = useState<any[]>([]);
  const [srcSystemsOrig,      setSrcSystemsOrig]      = useState<any>(null);
  const [hierarchies,         setHierarchies]         = useState<any[]>([]);
  const [relationships,       setRelationships]       = useState<any[]>([]);
  const [selectedDMEntityId,  setSelectedDMEntityId]  = useState<string | null>('');
  const [dataSrcSystemId,     setDataSrcSystemId]     = useState<string | null>('');
  const [selectedDMEntity,    setSelectedDMEntity]    = useState<any | null>(null);
  const [selectedDMFields,    setSelectedDMFields]    = useState<Record<string, Set<string>>>({});
  const [selectedDMPrimaryTable, setSelectedDMPrimaryTable] = useState('');

  // ── alert state ────────────────────────────────────────────────────────────
  const [showAlert,    setShowAlertState] = useState(false);
  const [message,      setMessage]        = useState('');
  const [error,        setError]          = useState('');
  const [bgBlock,      setBgBlock]        = useState(false);
  const [alertWClose,  setAlertWClose]    = useState(false);

  // ── modal state ────────────────────────────────────────────────────────────
  const [showLoginModal,              setShowLoginModal]              = useState(false);
  const [showInfaMDMMigrationModal,   setShowInfaMDMMigrationModal]   = useState(false);
  const [showReltioMDMMigrationModal, setShowReltioMDMMigrationModal] = useState(false);
  const [showDMERDiagramModal,        setShowDMERDiagramModal]        = useState(false);
  const [showDataModelTreeModal,      setShowDataModelTreeModal]      = useState(false);
  const [showChatAssistant,           setShowChatAssistant]           = useState(false);
  const [showDataSrcSystemModal,      setShowDataSrcSystemModal]      = useState(false);

  // ── misc state ─────────────────────────────────────────────────────────────
  const [isWaiting,                   setIsWaiting]                   = useState(false);
  const [rawMetaData,                 setRawMetaData]                 = useState<any>(null);
  const [assistantUseType,            setAssistantUseType]            = useState<string | null>('');
  const [migrationTool,               setMigrationTool]               = useState<string | null>(null);
  const [fieldFilter,                 setFieldFilter]                 = useState<{ field: string; entityId: string } | null>(null);
  const [selectedDGEntityIds,         setSelectedDGEntityIds]         = useState<string[]>([]);
  const [dgReviewActive,              setDgReviewActive]              = useState(false);
  const [mdmConfigMigrationOptionVal, setMdmConfigMigrationOptionVal] = useState('1');
  const [mdmConfigAppSelectedVal,     setMdmConfigAppSelectedVal]     = useState('');
  const [mdmConfigAppFile,            setMdmConfigAppFile]            = useState<File | null>(null);
  const [mdmConfigAppFileStr,         setMdmConfigAppFileStr]         = useState('');
  const [infaMDMConfigUploadSwitch,   setInfaMDMConfigUploadSwitch]   = useState(true);
  const [infaMDMChangeListFile,       setInfaMDMChangeListFile]       = useState<File | null>(null);
  const [infaMDMDataStatFile,         setInfaMDMDataStatFile]         = useState<File | null>(null);
  const [infaMDMSaaSProj,             setInfaMDMSaaSProj]             = useState<any>(null);
  const [infaMDMSaaSFolder,           setInfaMDMSaaSFolder]           = useState<any>(null);
  const [infaMDMSaaSConfigMigration,  setInfaMDMSaaSConfigMigration]  = useState<any>(null);
  const [idmcSession,                 setIdmcSession]                 = useState<any>(null);
  const [reltioMDMSaaSConfigMigration,setReltioMDMSaaSConfigMigration]= useState<any>(null);
  const [reltioSession,               setReltioSession]               = useState<any>(null);
  const [selectedDataModelFieldIds,   setSelectedDataModelFieldIds]   = useState<string[]>([]);
  const [componentResp,               setComponentResp]               = useState<any>({});

  // ─── derived helpers ───────────────────────────────────────────────────────

  const selectedApp = allAppList.find((a: any) => a.nm === appNm);

  // Source systems: prefer the lazily-fetched list; fall back to setup (so this
  // works whether or not the app-list payload still ships `setup`).
  const dataSrcSystems: { srcSystemId: string; srcSystemName: string }[] =
    sourceList.length
      ? sourceList.map(s => ({ srcSystemId: s.srcSystemId, srcSystemName: s.srcSystemName }))
      : (selectedApp?.setup
          ? Array.from(new Set(
              selectedApp.setup
                .filter((item: any) => item.dataSrcSystem)
                .map((item: any) => `${item.dataSrcSystem.srcSystemId}||${item.dataSrcSystem.srcSystemName}`)
            )).map((val: any) => {
              const [id, name] = val.split("||");
              return { srcSystemId: id, srcSystemName: name };
            })
          : []);

  // Lean entity shells for the ACTIVE source — fetched via getEntities (lazy),
  // falling back to setup. This is the single list MyComponent owns and passes
  // down so each secondary page renders the same top entity bar without re-fetching.
  const leanEntities = useMemo(() => {
    const base: { entityId: string; entity: string; srcId: string }[] =
      entityList.length
        ? entityList.map(e => ({ entityId: e.entityId, entity: e.entity || e.entityId, srcId: dataSrcSystemId || '' }))
        : (selectedApp?.setup ?? [])
            .filter((s: any) => !dataSrcSystemId || s.dataSrcSystem?.srcSystemId === dataSrcSystemId)
            .map((s: any) => ({ entityId: s.entityId, entity: s.entity || s.entityId, srcId: s.dataSrcSystem?.srcSystemId ?? dataSrcSystemId ?? '' }));
    return base.map(b => ({
      id:              b.entityId,
      entityId:        b.entityId,
      label:           b.entity,
      name:            b.entity,
      dataSrcSystemId: b.srcId,
      field:           [] as any[],
    }));
  }, [entityList, selectedApp, dataSrcSystemId]);

  // Entity tabs for the top bar (id/label only) — derived from the lean shells.
  const navEntities: { id: string; label: string }[] =
    leanEntities.map(e => ({ id: e.entityId, label: e.label }));

  // A stable, lightweight SHELL for the selected entity (no fields). Passed to
  // DataModelTree, which self-fetches the skeleton + paginated fields.
  const currentShell = useMemo(() => {
    if (!selectedEntityId) return null;
    const e = leanEntities.find(x => x.entityId === selectedEntityId);
    if (e) return e;
    // fallback: selection not in the loaded list yet
    return {
      id:              selectedEntityId,
      entityId:        selectedEntityId,
      label:           selectedEntityId,
      name:            selectedEntityId,
      dataSrcSystemId: dataSrcSystemId,
      field:           [] as any[],
    };
  }, [selectedEntityId, leanEntities, dataSrcSystemId]);

  // The CANONICAL entity's id. When the CANONICAL source is active we prefer the
  // entity the user actually selected in the top tab strip; otherwise fall back to
  // the first canonical entity in the current entity list, then setup.
  const canonicalEntityId = useMemo(() => {
    if (dataSrcSystemId?.toUpperCase() === 'CANONICAL' && selectedEntityId) return selectedEntityId;
    if (dataSrcSystemId?.toUpperCase() === 'CANONICAL' && entityList.length) return entityList[0].entityId;
    const s = (selectedApp?.setup ?? []).find(
      (x: any) => (x.dataSrcSystem?.srcSystemId ?? '').toUpperCase() === 'CANONICAL',
    );
    return s?.entityId ?? null;
  }, [dataSrcSystemId, selectedEntityId, entityList, selectedApp]);

  // Full entries loaded on demand (only for secondary tabs that need fields).
  const currentEntry = selectedEntityId
    ? (data.find((e: any) => e.entityId === selectedEntityId) ?? null)
    : null;

  // `dm` = loaded full entries for the active source (populated by build/save flows).
  const dm = dataSrcSystemId
    ? data.filter((e: any) => e.dataSrcSystemId === dataSrcSystemId)
    : data;

  // For secondary tabs (DQ Dashboard / Jobs / Lineage / Relationships): pass the
  // full lean entity list for the active source so each page can render the top
  // entity bar without fetching it itself. Prefer loaded full entries (`dm`) when
  // present; else the lean shells; else the single selected shell.
  const dmForTabs = dm.length ? dm : (leanEntities.length ? leanEntities : (currentShell ? [currentShell] : []));


  // ─── alert helpers ─────────────────────────────────────────────────────────

  const clearAlert = useCallback(() => {
    setMessage('');
    setError('');
    setAlertWClose(false);
  }, []);

  const showError = useCallback((msg: string, block = false, withClose = false) => {
    setError(msg); setMessage(''); setBgBlock(block); setAlertWClose(withClose);
  }, []);

  const showSuccess = useCallback((msg: string, block = false, withClose = false) => {
    setMessage(msg); setError(''); setBgBlock(block); setAlertWClose(withClose);
  }, []);

  // ─── entity / source selection ──────────────────────────────────────────────
  // Selecting an entity sets the ids only. The DM tree (DataModelTree) self-fetches
  // its skeleton + paginated fields, so nothing heavy loads here. Secondary tabs
  // that need the full entry load it on demand via the effect below.

  const selectEntity = useCallback((_appName: string, entityId: string) => {
    if (!entityId) return;
    setSelectedEntityId(entityId);
    setSelectedDMEntityId(entityId);   // everything is keyed by entityId now
  }, []);

  // Switch source system → reset to that source's first entity (picked by the
  // entity-list effect once getEntities resolves for the new source).
  const selectSource = useCallback((srcSystemId: string) => {
    // Tab reset is handled centrally by the sidebar-context effect below (→ first tab),
    // AFTER dataSrcSystemId actually changes — computing it here read stale sidebar items.
    setDataSrcSystemId(srcSystemId);
    setSelectedEntityId('');
    setSelectedDMEntityId('');
  }, []);

  // ── Lazy source + entity lists ─────────────────────────────────────────────
  // Sources for the app (replaces deriving from setup).
  useEffect(() => {
    if (!appNm) { setSourceList([]); return; }
    let cancelled = false;
    getSources(appNm)
      .then(res => { if (!cancelled) setSourceList(res?.success ? (res.data || []) : []); })
      .catch(() => { if (!cancelled) setSourceList([]); });
    return () => { cancelled = true; };
  }, [appNm]);

  // Entity list for the active source (the lean DM the top bar renders from).
  useEffect(() => {
    if (!appNm || !dataSrcSystemId) { setEntityList([]); return; }
    let cancelled = false;
    getEntities(appNm, dataSrcSystemId)
      .then(res => { if (!cancelled) setEntityList(res?.success ? (res.data || []) : []); })
      .catch(() => { if (!cancelled) setEntityList([]); });
    return () => { cancelled = true; };
  }, [appNm, dataSrcSystemId]);

  // Once the entity list arrives, auto-select the first entity if the current
  // selection isn't part of it (covers app-open and source-switch).
  useEffect(() => {
    if (!entityList.length) return;
    if (selectedEntityId && entityList.some(e => e.entityId === selectedEntityId)) return;
    const first = entityList[0].entityId;
    setSelectedEntityId(first);
    setSelectedDMEntityId(first);
  }, [entityList, selectedEntityId]);

  // NOTE: secondary tabs (DQ Dashboard, Jobs, Lineage, Relationships, Canonical)
  // are passed a lightweight entity shell and are expected to fetch their own data
  // by appNm + entityId. We intentionally do NOT eagerly pull the full refinedDM
  // entry (which would load every sub-entity + every field) — that defeats lazy
  // loading for entities with 100+ tables.

  // ─── app list ──────────────────────────────────────────────────────────────

  const getAppList = useCallback((t: string) => {
    adriva_getAppList(t,
      (res: any) => {
        if (res.success && t === '') setAllAppList(res.data);
        else if (res.success && t !== '') setToolAppList(res.data);
      },
      (err: any) => console.log(err),
    );
  }, []);

  useEffect(() => { getAppList(''); }, []);

  const refreshAppList = useCallback(() => { getAppList(tool); }, [tool, getAppList]);

  // ─── handlePostData ────────────────────────────────────────────────────────

  const handlePostData = useCallback((d: string) => {
    const pData = JSON.parse(d);
    adriva_postAction(d,
      (res: any) => {
        if (res.success) {
          let newDataSrcSystemId = dataSrcSystemId;
          let newSelectedDMEntityId = selectedDMEntityId;
          const effectiveTool = pData.selected_tool || tool;
          const effectiveAppNm = pData.data.nm || appNm;
          const refinedDM = res.data.refinedDM;

          const freshAllAppList = allAppList;
          const foundApp = freshAllAppList.filter((a: any) => a.nm === effectiveAppNm)[0];

          if (!newDataSrcSystemId && foundApp?.setup?.length > 0) {
            newDataSrcSystemId = foundApp.setup[0].dataSrcSystem?.srcSystemId ?? '';
            const srcDM = refinedDM.filter((e: any) => e.dataSrcSystemId === newDataSrcSystemId)[0];
            newSelectedDMEntityId = srcDM ? srcDM.id : refinedDM?.length > 0 ? refinedDM[0].id : '';
          } else {
            newSelectedDMEntityId = refinedDM?.length > 0 ? refinedDM[0].id : '';
          }

          setPageName('');
          setToolState(effectiveTool);
          setAppNm(effectiveAppNm);
          setData(res.data.refinedDM || []);
          setOrigData(res.data.refinedDM || []);
          setSrcSystems(res.data.srcSystem || []);
          setSrcSystemsOrig(res.data.srcSystem || []);
          setHierarchies(res.data.hierarchies || []);
          setRelationships(res.data.relationships || []);
          setSelectedDMEntityId(newSelectedDMEntityId);
          // keep the lazy key in sync — currentEntry is matched by entityId
          const selEntry = (refinedDM || []).find((e: any) => e.id === newSelectedDMEntityId);
          setSelectedEntityId(selEntry?.entityId ?? '');
          setDataSrcSystemId(newDataSrcSystemId);
          setShowDMERDiagramModal(false);
          showSuccess('Successfully Processed');
          setSelectedDGEntityIds([]);
          setDgReviewActive(false);
        } else {
          showError(res.msg);
        }
      },
      (err: any) => console.log(err),
    );
  }, [dataSrcSystemId, selectedDMEntityId, tool, appNm, allAppList, showSuccess, showError]);

  // ─── navigation helpers ────────────────────────────────────────────────────

  const handleNavigateToField = useCallback((p: NavigateToFieldPayload) => {
    const fromSources = sourceList.find(s => s.srcSystemName === p.srcSystem)?.srcSystemId;
    const matchingSetup = (selectedApp?.setup ?? []).find((s: any) => s.dataSrcSystem?.srcSystemName === p.srcSystem);
    const newDataSrcSystemId = fromSources ?? matchingSetup?.dataSrcSystem?.srcSystemId ?? dataSrcSystemId;
    // This navigation targets the DG tab. If it also switches source, tell the reset
    // effect to keep 'dg' instead of snapping to the first tab.
    if (newDataSrcSystemId && newDataSrcSystemId !== dataSrcSystemId) pendingTabRef.current = 'dg';
    setTabKey('dg');
    setDataSrcSystemId(newDataSrcSystemId ?? null);
    // lazy-load the target entity if it isn't already cached
    if (p.entityId) selectEntity(appNm, p.entityId);
    setFieldFilter({ field: p.field, entityId: p.entityId });
  }, [sourceList, selectedApp, dataSrcSystemId, appNm, selectEntity]);

  // ─── sidebar items ─────────────────────────────────────────────────────────

  const renderSidebarItems = useCallback((): SidebarItem[] => {
    if (appNm === 'DQ_BARRY_MAT'){
      return [
        { key: 'lkp', label: 'Reference Data', icon: '🏷️' },
        { key: 'ekd', label: 'Enterprise Knowledge', icon: '📋' },
        { key: 'bc', label: 'Business Control', icon: '🗺️' },
        { key: 'sdhWorkspace', label: 'DQ Rules', icon: '🧪' },
        //{ key: 'sdhWorkspaceV2', label: 'DQ Rules v2', icon: '✨' },
      ];
    }
    if (tool === 'BUILD_DG_USING_DATA') {
      return [
        { key: 'businessEntity', label: 'Technical Metadata', icon: '🗄️' },
        { key: 'glossary',       label: 'Business Glossary',  icon: '📖' },
        { key: 'dg',             label: 'DG Artifacts',       icon: '✅' },
        { key: 'jobMonitoring',  label: 'Jobs',               icon: '👁' },
      ];
    }
    const items: SidebarItem[] = [];
    items.push({
      key: 'businessEntity',
      label: dataSrcSystemId === 'CANONICAL' ? 'Business Entity' : 'DQ Rules',
      icon: '🗄️',
    });
    if (dataSrcSystemId === 'CANONICAL') {
      items.push({ key: 'canonicalRefData',     label: 'Reference Data', icon: '🏷️' });
      items.push({ key: 'dataLineageCrossWalk', label: 'Data Lineage',   icon: '🗺️' });
      items.push({ key: 'relHierarchy',         label: 'Relationships',  icon: '🌐' });
    }
    items.push({ key: 'lkp', label: 'Reference Data', icon: '📋' });
    //items.push({ key: 'sdhWorkspace', label: 'DQ Rules (New)', icon: '🧪' });

    //items.push({ key: 'ekd', label: 'Enterprise Knowledge', icon: '📋' });
    //items.push({ key: 'bc', label: 'Business Control', icon: '📋' });
    
    items.push({ key: 'dqDashboard',   label: 'DQ Dashboard', icon: '📊' });
    if (tool === 'BUILD_DQ_USING_DATA'){
      items.push({ key: 'migrationAssessment', label: 'Migration Assessment', icon: '🚀' });
    }
    items.push({ key: 'jobMonitoring', label: 'Jobs',         icon: '👁' });
    if (tool === 'BUILD_MDM_USING_DATA' && dataSrcSystemId !== 'CANONICAL') {
      items.push({ key: 'dataLineageCrossWalk', label: 'Data Lineage',   icon: '🗺️' });
      //items.push({ key: 'lkp',          label: 'Reference Data', icon: '📋' });
      //items.push({ key: 'srcSystem',    label: 'Source Systems', icon: '🔌' });
      //items.push({ key: 'relHierarchy', label: 'Relationships',  icon: '🌐' });
    }
    return items;
  }, [tool, appNm, dataSrcSystemId]);

  // When the sidebar CONTEXT changes (tool / app / source) the set of tabs can change, so snap the
  // active tab back to the first item. This runs AFTER the state settles, so renderSidebarItems()
  // reflects the NEW context (the old imperative reset in the handlers read stale items and only worked
  // when the keys happened to differ). A navigation that wants a specific tab (e.g. "go to field" → DG)
  // sets pendingTabRef to opt out of the first-tab snap.
  useEffect(() => {
    const items = renderSidebarItems();
    if (!items.length) return;
    const want = pendingTabRef.current;
    pendingTabRef.current = null;
    // A navigation may pin a specific tab (e.g. 'dg'); otherwise snap to the first item.
    setTabKey(want && items.some(it => it.key === want) ? want : items[0].key);
  }, [tool, appNm, dataSrcSystemId, renderSidebarItems]);

  // ─── DM update / save ──────────────────────────────────────────────────────

  const buildSrcSystemRank = useCallback((node: any, sysRnkId: number,
    srcSysFldGrpCon: Record<string, SystemRank>, idmcSysRank: any) => {
    const PRFX = 'R';
    const srcSystemDict = Object.fromEntries(srcSystems.map((item: any) => [item.id, item]));
    const srcSysFldGrp: Record<string, SystemRank> = {};

    const getIDMCRnk = (srcCds: string) => {
      if (!idmcSysRank) return undefined;
      let mtch = '';
      Object.keys(idmcSysRank).forEach(r => { if (idmcSysRank[r].join(';') === srcCds) mtch = r; });
      if (mtch) return mtch;
      const all = Array.from({ length: 100 }, (_, i) => `R${i + 1}`);
      const missing = all.filter(r => !Object.keys(idmcSysRank).includes(r));
      if (missing.length > 0) { idmcSysRank[missing[0]] = srcCds.split(';'); return missing[0]; }
    };

    node.field?.filter((f: any) => f.eClass === "Field").forEach((field: any) => {
      const sysIdOrder = field.SYSTEM_ORDER;
      if (sysIdOrder) {
        let sysOrderRnk = srcSysFldGrp[sysIdOrder];
        if (!sysOrderRnk) {
          const srcCdList = sysIdOrder.split(";")
            .filter((item: any) => item in srcSystemDict)
            .map((item: any) => srcSystemDict[item].code);
          let rnkGuid = `${PRFX}_${sysRnkId}`;
          if (idmcSysRank) rnkGuid = getIDMCRnk(srcCdList.join(";")) as string;
          else if (node.SYSTEM_ORDER?.[sysIdOrder]) rnkGuid = node.SYSTEM_ORDER[sysIdOrder].rnk;
          else if (srcSysFldGrpCon[sysIdOrder]) rnkGuid = srcSysFldGrpCon[sysIdOrder].rnk;
          else sysRnkId += 1;
          sysOrderRnk = { rnk: rnkGuid, srcCd: srcCdList.join(";"), srcId: sysIdOrder, flds: [] };
          srcSysFldGrp[sysIdOrder] = sysOrderRnk;
        }
        field.SYSTEM_ORDER_GUID = sysOrderRnk.rnk;
        field.SYSTEM_ORDER_CD   = sysOrderRnk.srcCd;
        srcSysFldGrp[sysIdOrder].flds.push(field.TABLE_NM + ' - ' + field.COLUMN_NAME);
      } else {
        field.SYSTEM_ORDER_GUID = "";
        field.SYSTEM_ORDER_CD   = "";
      }
    });

    node.SYSTEM_ORDER      = srcSysFldGrp;
    node.SYSTEM_ORDER_LIST = Object.values(srcSysFldGrp).sort((a: any, b: any) => b.flds.length - a.flds.length);

    if (Object.keys(srcSysFldGrpCon).length === 0) {
      Object.assign(srcSysFldGrpCon, JSON.parse(JSON.stringify(srcSysFldGrp)));
    } else {
      Object.keys(srcSysFldGrp).forEach(sysId => {
        if (srcSysFldGrpCon[sysId]) srcSysFldGrpCon[sysId].flds = srcSysFldGrpCon[sysId].flds.concat(JSON.parse(JSON.stringify(srcSysFldGrp[sysId].flds)));
        else srcSysFldGrpCon[sysId] = JSON.parse(JSON.stringify(srcSysFldGrp[sysId]));
      });
    }
    node.field?.filter((f: any) => f.eClass === "FieldGroup").forEach((fg: any) => {
      buildSrcSystemRank(fg, sysRnkId, srcSysFldGrpCon, idmcSysRank);
    });
  }, [srcSystems]);

  const handleUpdateDMData = useCallback((uData: any) => {
    const newData = [...data];
    let srcSysFldGrpCon: Record<string, SystemRank> = {};
    newData.forEach((entity: any, index: number) => {
      if (uData[0].id === entity.id) {
        newData[index] = uData[0];
        buildSrcSystemRank(newData[index], 1, srcSysFldGrpCon, null);
      }
    });
    setData(newData);
    showSuccess('successfully updated');
  }, [data, buildSrcSystemRank, showSuccess]);

  const handleSaveDMData = useCallback(() => {
    const postData = {
      action: "UPDATE_DM", appNm,
      data: { dm: data, srcSystem: srcSystems, relationships, hierarchies }
    };
    handlePostData(JSON.stringify(postData));
  }, [appNm, data, srcSystems, relationships, hierarchies, handlePostData]);

  // ─── source system ─────────────────────────────────────────────────────────

  const handleSrcSystemValueChanges = useCallback((newSrcSystems: any) => {
    const adjustFieldSurvivor = (node: any, systems: any) => {
      let flds = node.field?.filter((f: any) => f.eClass === 'Field') || [];
      flds.forEach((f: any) => {
        if (f.SYSTEM_ORDER) {
          f.SYSTEM_ORDER = f.SYSTEM_ORDER.split(';')
            .filter((s: string) => systems.some((sys: any) => sys.id === s))
            .join(';');
        }
      });
      node.field?.filter((f: any) => f.eClass === 'FieldGroup').forEach((fg: any) => adjustFieldSurvivor(fg, systems));
    };
    const newData = [...data];
    newData.forEach((e: any) => adjustFieldSurvivor(e, newSrcSystems));
    setData(newData);
    setSrcSystems(newSrcSystems);
  }, [data]);

  // ─── DataModelTreeModal ────────────────────────────────────────────────────

  const handleOpenDataModelTreeModal = useCallback(() => {
    const selEntity: any = data.find((e: any) => e.id === selectedDMEntityId);
    if (!selEntity) return;
    const newSelectedDMFields: Record<string, Set<string>> = {};
    const processFields = (fields: any[]) => {
      fields.forEach((field: any) => {
        if (field.eClass === 'Field' && field['FLD_PHYSICAL_ID']) {
          const [tableName, columnName] = field['FLD_PHYSICAL_ID'].split('-');
          if (!newSelectedDMFields[tableName]) newSelectedDMFields[tableName] = new Set();
          newSelectedDMFields[tableName].add(columnName);
        } else if (field.eClass === 'FieldGroup' && field.field) processFields(field.field);
      });
    };
    if (selEntity.field) processFields(selEntity.field);
    const primary = Object.keys(newSelectedDMFields)[0];
    setSelectedDMFields(newSelectedDMFields);
    setShowDMERDiagramModal(true);
    setSelectedDMPrimaryTable(primary);
    setSelectedDMEntity({ name: selEntity.name, label: selEntity.label });
  }, [data, selectedDMEntityId]);

  const handleSaveDataModelTreeModalData = useCallback((newSelectedAdd: string[], newSelectedRemove: string[]) => {
    let newData = [...data];
    let ids = [...selectedDataModelFieldIds];
    for (const c of newSelectedAdd) {
      const om = findField(origData, c);
      const omm = findField(newData, c);
      if (om && om.p && !ids.includes(c) && !omm) {
        const nm = findField(newData, om.p.id);
        if (nm) {
          const fld = JSON.parse(JSON.stringify(om.c));
          if (om.c.eClass === 'FieldGroup') fld.field = [];
          const fg = nm.c.field.filter((f: any) => f.eClass === 'FieldGroup');
          fg.length > 0 ? nm.c.field.splice(nm.c.field.indexOf(fg[0]), 0, fld) : nm.c.field.push(fld);
        }
        ids.push(c);
      }
    }
    for (const c of newSelectedRemove) {
      const nm = findField(newData, c);
      if (nm && nm.p && ids.includes(c)) {
        nm.p.field.splice(nm.p.field.indexOf(nm.c), 1);
        ids = ids.filter(x => x !== c);
      }
    }
    setData(newData);
    setSelectedDataModelFieldIds(ids);
    setShowDataModelTreeModal(false);
  }, [data, origData, selectedDataModelFieldIds]);

  // ─── rel/hierarchy ─────────────────────────────────────────────────────────

  const handleRelAndHierarchySave = useCallback((rel: any, hier: any) => {
    const rels = [...relationships];
    const hiers = [...hierarchies];
    const mRel = rels.find((r: any) => r['@id'] === rel['@id']);
    if (mRel) rels[rels.indexOf(mRel)] = rel; else rels.push(rel);
    if (!hiers.find((h: any) => h['@id'] === hier['@id'])) hiers.push(hier);
    setRelationships(rels);
    setHierarchies(hiers);
  }, [relationships, hierarchies]);

  // ─── app selected ──────────────────────────────────────────────────────────
  // Lazy: do NOT load the whole app.json refinedDM. Just set the app context and
  // reset caches; the first entity's DM is fetched on demand by the effect below
  // (or when the user clicks a source pill / entity tab).

  const handleAppSelected = useCallback((nm: string) => {
    getAppList('');
    getAppList(tool);
    setMdmConfigAppSelectedVal(nm);
    setAppNm(nm);
    setPageName('');
    setData([]);
    setOrigData([]);
    setSourceList([]);
    setEntityList([]);
    setSelectedEntityId('');
    setSelectedDMEntityId('');
    setDataSrcSystemId('');
    setSelectedDGEntityIds([]);
    setDgReviewActive(false);
  }, [tool, getAppList]);

  // Once the app is set, pick the first source. The entity-list effect then loads
  // that source's entities and auto-selects the first one. Falls back to setup for
  // the first source if the lazy source list hasn't arrived.
  useEffect(() => {
    if (!appNm || dataSrcSystemId) return;
    const firstSrc =
      sourceList[0]?.srcSystemId
      ?? (selectedApp?.setup ?? [])[0]?.dataSrcSystem?.srcSystemId
      ?? '';
    if (firstSrc) setDataSrcSystemId(firstSrc);
  }, [appNm, dataSrcSystemId, sourceList, selectedApp]);

  // ─── MDM config ────────────────────────────────────────────────────────────

  const handleUpdateMDMConfig = useCallback((topTableName: string, config: any, beImported: boolean) => {
    if (beImported) {
      const newData = [...data, config];
      const newSrcSystems = [...srcSystems];
      cleanDm(newData, newSrcSystems);
      setData(newData);
      setSelectedDMEntityId(config.id);
      setSelectedEntityId(config.entityId ?? '');
      setSrcSystems(newSrcSystems);
      setShowDMERDiagramModal(false);
    } else {
      adriva_convertToRefinedDM(appNm, topTableName, JSON.stringify(config), JSON.stringify(srcSystems),
        (res: any) => {
          if (res.success) {
            const newData = [...data];
            if (selectedDMEntity) {
              const tgt = newData.find((e: any) => e.id === selectedDMEntityId);
              if (tgt) newData[newData.indexOf(tgt)] = mergeDMJSON(tgt, res.data["dm"]);
            } else {
              newData.push(res.data["dm"]);
            }
            const newSrcSystems = [...srcSystems];
            cleanDm(newData, newSrcSystems);
            setData(newData);
            setSelectedDMEntityId(res.data["dm"].id);
            setSelectedEntityId(res.data["dm"].entityId ?? '');
            setSrcSystems(newSrcSystems);
            setShowDMERDiagramModal(false);
          }
        },
        (err: any) => console.log(err),
      );
    }
  }, [appNm, data, srcSystems, selectedDMEntity, selectedDMEntityId]);

  const handleUploadMDMConfig = useCallback((dataStr: string | null, nm?: string) => {
    const postData = {
      action: "MDM_APP_UPLOADED",
      selected_tool: tool,
      data: { nm: nm || mdmConfigAppSelectedVal, data: dataStr }
    };
    handlePostData(JSON.stringify(postData));
    setShowDMERDiagramModal(true);
  }, [tool, mdmConfigAppSelectedVal, handlePostData]);

  // ─── migration helpers (unchanged logic, converted to use state setters) ───

  const buildMigrationList = useCallback((dmData: any[]) => {
    const lkpRS: any[] = [];
    let sysRanks: any[] = [];
    dmData.forEach((e: any) => sysRanks = sysRanks.concat(JSON.parse(JSON.stringify(e['SYSTEM_ORDER_LIST'] || []))));

    const getDMMigra = (e: any, d: any): any => {
      const n = {
        value: d.name, label: d.label,
        children: (d.field?.filter((i: any) => i.eClass === 'Field') || []).map((i: any) => ({
          value: `${i.TABLE_NM}-${i.COLUMN_NAME}`,
          label: `${i.COLUMN_DISPLAY_NAME} - ${i.COLUMN_NAME}`,
        })),
      };
      d.field?.filter((i: any) => i.eClass === 'Field').forEach((i: any) => {
        if (i.LOOKUP_RDS_NM) {
          const mtch = lkpRS.find((j: any) => j.value === i.LOOKUP_RDS_NM);
          const child = { value: `LKP_COL_${e.name}|${d.name}|${i.TABLE_NM}-${i.COLUMN_NAME}`, label: `${e.label} -> ${i.TABLE_DISPLAY_NM} -> ${i.COLUMN_DISPLAY_NAME}` };
          if (mtch) mtch.children.push(child);
          else lkpRS.push({ value: i.LOOKUP_RDS_NM, label: `${i.LOOKUP_RDS_DISP_NM} - ${i.LOOKUP_RDS_NM}`, children: [child] });
        }
      });
      d.field?.filter((i: any) => i.eClass === 'FieldGroup').forEach((i: any) => { n.children.push(getDMMigra(e, i)); });
      return n;
    };

    const dmMigrt: any[] = [];
    dmData.forEach((e: any) => dmMigrt.push(getDMMigra(e, e)));
    return [
      { value: 'srcSystem', label: `Source Systems (${srcSystems.length})`, children: srcSystems.map((i: any) => ({ value: i.id, label: `${i.name} - ${i.code}` })) },
      { value: 'lkpRS', label: `Reference Sets (${lkpRS.length})`, children: lkpRS },
      { value: 'sysRanks', label: `System Ranks (${sysRanks.length})`, children: sysRanks.map((i: any) => ({ value: i.rnk, label: `${i.rnk} => ${i.srcCd} - ${i.flds.length}`, children: i.flds.map((f: any) => ({ value: `SYS_RNK_FLD_${f}`, label: f })) })) },
      { value: 'dm', label: 'Data Model', children: dmMigrt },
      { value: 'relHier', label: 'Hierarchy & Relationship', children: hierarchies.map((i: any) => ({ value: `HIER_${i.name}`, label: i.label, children: relationships.filter((r: any) => r['@parentId'] === i['@id']).map((f: any) => ({ value: `HIER_REL_${f.name}`, label: f.label })) })) },
    ];
  }, [srcSystems, hierarchies, relationships]);

  const handleMigrateDMData = useCallback(() => {
    if (migrationTool === 'MIGRATION_INFA_LEGACY_TO_IDMC') handleMigrateDMDataToInfaSaaS();
    else handleMigrateDMDataToReltioSaaS();
  }, [migrationTool]);

  const handleMigrateDMDataToInfaSaaS = useCallback(() => {
    const canonical = data.filter((e: any) => e.dataSrcSystemId?.toUpperCase() === 'CANONICAL');
    let srcSysFldGrpCon: Record<string, SystemRank> = {};
    canonical.forEach((e: any) => buildSrcSystemRank(e, 2, srcSysFldGrpCon, null));
    setInfaMDMSaaSConfigMigration(buildMigrationList(canonical));
    setShowInfaMDMMigrationModal(true);
  }, [data, buildSrcSystemRank, buildMigrationList]);

  const handleMigrateDMDataToReltioSaaS = useCallback(() => {
    const canonical = data.filter((e: any) => e.dataSrcSystemId?.toUpperCase() === 'CANONICAL');
    let srcSysFldGrpCon: Record<string, SystemRank> = {};
    canonical.forEach((e: any) => buildSrcSystemRank(e, 2, srcSysFldGrpCon, null));
    setReltioMDMSaaSConfigMigration(buildMigrationList(canonical));
    setShowReltioMDMMigrationModal(true);
  }, [data, buildSrcSystemRank, buildMigrationList]);

  const handleLoginToSaaS = useCallback((u: string, p: string, c: string, s: string) => {
    if (tool === 'MIGRATION_INFA_LEGACY_TO_IDMC' || migrationTool === 'MIGRATION_INFA_LEGACY_TO_IDMC') {
      setIsWaiting(true); showSuccess('In Progress');
      idmc_login(u, p, (res: any) => {
        setIsWaiting(false); showSuccess('Successfully logged in');
        setIdmcSession(res.data);
        idmc_getProjectList(res.data['IDMC_SESSION_ID'], res.data['IDMC_BASE_APP_URL'],
          (r: any) => { setInfaMDMSaaSProj(r.data); setShowLoginModal(false); setShowInfaMDMMigrationModal(true); },
          (err: any) => { setIsWaiting(false); showError('Failed to get project list'); });
      }, () => { setIsWaiting(false); showError('Invalid Credential'); });
    } else {
      setIsWaiting(true); showSuccess('In Progress');
      reltio_login(c, s, u, p, (res: any) => {
        if (res.success) {
          setIsWaiting(false); showSuccess('Successfully logged in');
          setReltioSession({ CLIENT_ID: c, CLIENT_SECRET: s, USER_ID: u, USER_PWD: p, ACCESS_TOKEN: res.data, MDM_TENANT: '', RDM_TENANT: '' });
          setShowLoginModal(false);
        }
      }, () => { setIsWaiting(false); showError('Invalid Credential'); });
    }
  }, [tool, migrationTool, showSuccess, showError]);

  const pullInfaSaaSFolderList = useCallback((projId: string) => {
    if (!idmcSession) return;
    setIsWaiting(true);
    idmc_getFolderList(idmcSession['IDMC_SESSION_ID'], idmcSession['IDMC_BASE_APP_URL'], projId,
      (res: any) => { setIsWaiting(false); setInfaMDMSaaSFolder(res.data); },
      () => { setIsWaiting(false); showError('Failed to get folder list'); });
  }, [idmcSession, showError]);

  const handleTriggerInfaMDMSaaSMigration = useCallback((selProj: string, selFolder: string) => {
    if (!idmcSession) { setShowLoginModal(true); return; }
    idmc_getSourceRank(idmcSession['IDMC_SESSION_ID'], idmcSession['IDMC_BASE_APP_URL'], '',
      (res: any) => {
        const newData = [...data];
        let srcSysFldGrpCon: Record<string, SystemRank> = {};
        newData.forEach((e: any) => buildSrcSystemRank(e, 1, srcSysFldGrpCon, res.data));
        setData(newData);
        setInfaMDMSaaSConfigMigration(buildMigrationList(newData));
        const config = { dm: newData, srcSystem: srcSystems };
        setIsWaiting(true); showSuccess('Migration in Progress..', true);
        idmc_migrate(idmcSession['IDMC_SESSION_ID'], idmcSession['IDMC_BASE_APP_URL'], selFolder, JSON.stringify(config),
          (r: any) => {
            setIsWaiting(false);
            if (!r.success) showError(r.data?.errorSummary || 'Error occurred', true, true);
            else showSuccess('Successfully Migrated', true, true);
          },
          () => { setIsWaiting(false); showError('Migration failed', false); });
      },
      () => showError('Failed to get source rank'));
  }, [idmcSession, data, srcSystems, buildSrcSystemRank, buildMigrationList, showSuccess, showError]);

  const handleTriggerReltioMDMSaaSMigration = useCallback((exisConfig: string | null, migrate: boolean, mdmTenant: string, rdmTenant: string) => {
    if (!reltioSession && migrate) { setShowLoginModal(true); return; }
    const sess = { ...reltioSession, MDM_TENANT: mdmTenant, RDM_TENANT: rdmTenant };
    setReltioSession(sess);
    const config = { dm: data, srcSystem: srcSystems, relationships, hierarchies };
    setIsWaiting(true); showSuccess('Migration in Progress..', true);
    if (migrate) {
      reltio_migrate(JSON.stringify(sess), JSON.stringify(config), exisConfig,
        (r: any) => {
          setIsWaiting(false);
          if (!r.success) showError(r.message, true, true);
          else { showSuccess('Successfully Migrated', true, true); downloadJsonFile(r.data); }
        },
        () => { setIsWaiting(false); showError('Migration failed'); });
    } else {
      get_reltio_migrate(JSON.stringify(config), exisConfig,
        (r: any) => {
          setIsWaiting(false);
          if (!r.success) showError('Config generation failed');
          else { showSuccess('Successfully L3 Config generated.', true, true); downloadJsonFile(r.data); }
        },
        () => { setIsWaiting(false); showError('Config generation failed'); });
    }
  }, [reltioSession, data, srcSystems, relationships, hierarchies, showSuccess, showError]);

  // ─── tool setter ───────────────────────────────────────────────────────────

  const setTool = useCallback((t: string) => {
    setToolState(t);
    getAppList(t);
    if (t === 'BUILD_MDM_USING_NLP')    setAssistantUseType('BUILD_NEW_ENTITY');
    else if (t === 'BUILD_MDM_USING_DATA') setAssistantUseType('DDM_BUILD_NEW_ENTITY');
  }, [getAppList]);

  // ─── tab content ───────────────────────────────────────────────────────────

  const renderTabContent = useCallback(() => {
    switch (tabKey) {

      case 'businessEntity':
        return (
          <div className="tab-content p-3">
            <EntityNavBar
              entities={navEntities}
              selectedId={selectedEntityId || ''}
              onSelect={(eid) => selectEntity(appNm, eid)}
            />
            <div style={{ overflowY: 'hidden' }}>
              {(currentShell ?? currentEntry) ? (
                <DataModelTree
                  key={selectedEntityId}
                  selectedApp={selectedApp}
                  tool={tool}
                  selectedDMEntityId={selectedEntityId ?? null}
                  dataSrcSystemId={dataSrcSystemId}
                  dataModelData={[currentShell ?? currentEntry]}
                  srcSystems={srcSystems}
                  appNm={appNm}
                  handleUpdateDMData={handleUpdateDMData}
                  handleOpenDataModelTreeModal={handleOpenDataModelTreeModal}
                />
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: '#445566' }}>
                  Select an entity to view its data model.
                </div>
              )}
            </div>
          </div>
        );

      case 'dqDashboard':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <DQDashboardContainer appNm={appNm} dmData={dmForTabs} />
            </div>
          </div>
        );

      case 'migrationAssessment':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <DQAssessmentReport appNm={appNm} entityId={selectedEntityId} entityLabel={currentShell?.label} />
            </div>
          </div>
        );

      case 'sdhWorkspaceV2':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <DqRulesV2
                appNm={appNm}
                entityId={selectedEntityId}
                entityLabel={currentShell?.label}
              />
            </div>
          </div>
        );

      case 'sdhWorkspace':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <SdhReviewWorkspace
                appNm={appNm}
                entityId={selectedEntityId}
                entityLabel={currentShell?.label}
              />
            </div>
          </div>
        );

      case 'lkp':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <LookupWorkbench
                appNm={appNm}
                entityId={selectedEntityId}
                entityLabel={currentShell?.label}
              />
            </div>
          </div>
        );

      case 'ekd':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <EkdReview
                entityId={selectedEntityId}
                entityLabel={currentShell?.label}
              />
            </div>
          </div>
        );

      case 'bc':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <BcReview
                entityId={selectedEntityId}
                entityLabel={currentShell?.label}
              />
            </div>
          </div>
        );

      case 'srcSystem':
        return (
          <div className="p-3">
            {['MIGRATION_INFA_LEGACY_TO_RELTIO', 'MIGRATION_INFA_LEGACY_TO_IDMC'].includes(tool) &&
              <SourceSystems data={srcSystems} handleSrcSystemValueChanges={handleSrcSystemValueChanges} />}
            {tool === 'BUILD_MDM_USING_DATA' &&
              <MDMSourceManager sources={srcSystems} dm={data} app={selectedApp}
                handleSrcSystemValueChanges={handleSrcSystemValueChanges}
                refreshAppList={refreshAppList} />}
          </div>
        );

      case 'relHierarchy':
        return (
          <div className="tab-content p-3">
            <EntityHierRelationship appNm={appNm} entities={data}
              hierarchies={hierarchies} relationships={relationships}
              handleRelAndHierarchySave={handleRelAndHierarchySave} />
          </div>
        );

      case 'glossary':
        return (
          <div style={{ height: "1130px", overflowY: "auto" }}>
            {tool === 'BUILD_DG_USING_DATA' && (
              <BusinessGlossaryPage appNm={appNm} onNavigateToField={handleNavigateToField} />
            )}
          </div>
        );

      case 'dg': {
        if (tool !== 'BUILD_DG_USING_DATA') {
          return <DataGovernanceCatalog glossary={[]} policyMappings={[]} />;
        }
        const entitiesInSource = (entityList.length
          ? entityList.map((e) => ({ entityId: e.entityId, entity: e.entity, domain: e.domain }))
          : (selectedApp?.setup ?? [])
              .filter((s: any) => !dataSrcSystemId ? true : s.dataSrcSystem?.srcSystemId === dataSrcSystemId)
              .map((s: any) => ({ entityId: s.entityId, entity: s.entity, domain: s.domain })));
        const effectiveSrcId = dataSrcSystemId ?? sourceList[0]?.srcSystemId ?? selectedApp?.setup?.[0]?.dataSrcSystem?.srcSystemId ?? '';
        const effectiveSrcName = (sourceList.find(s => s.srcSystemId === effectiveSrcId)?.srcSystemName)
          ?? (selectedApp?.setup ?? [])
              .find((s: any) => s.dataSrcSystem?.srcSystemId === effectiveSrcId)?.dataSrcSystem?.srcSystemName
          ?? effectiveSrcId;
        return (
          <div style={{ height: '1130px', overflowY: 'auto', background: '#eef1f6' }}>
            {!dgReviewActive ? (
              <SourceDashboard appNm={appNm} srcSystemId={effectiveSrcId} srcSystemName={effectiveSrcName}
                entitiesInSource={entitiesInSource} initialSelectedIds={selectedDGEntityIds}
                onOpenReview={(ids) => { setSelectedDGEntityIds(ids); setDgReviewActive(true); }} />
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 18px', background: '#fff', borderBottom: '1px solid #c8d4e0' }}>
                  <button onClick={() => setDgReviewActive(false)}
                    style={{ background: 'transparent', border: '1px solid #c8d4e0', color: '#1e3a6e',
                      borderRadius: 6, padding: '5px 14px', fontSize: '0.74rem', fontWeight: 600,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    ← Source Dashboard
                  </button>
                  <div style={{ fontSize: '0.74rem', color: '#445566' }}>
                    Reviewing <strong style={{ color: '#1e3a6e' }}>{selectedDGEntityIds.length}</strong>
                    {' '}entit{selectedDGEntityIds.length === 1 ? 'y' : 'ies'}
                  </div>
                </div>
                <GovernanceArtifacts appNm={appNm} entityId={selectedDGEntityIds[0] ?? ''}
                  lazyAppNm={appNm} lazyEntityIds={selectedDGEntityIds}
                  lazyEntityLabels={Object.fromEntries(
                    entitiesInSource.map((e:any) => [e.entityId, e.entity])
                  )}
                  initialFieldFilter={
                    fieldFilter?.entityId && selectedDGEntityIds.includes(fieldFilter.entityId)
                      ? fieldFilter.field : undefined}
                  onFieldFilterClear={() => setFieldFilter(null)} />
              </>
            )}
          </div>
        );
      }

      case 'jobMonitoring':
        return (
          <div className="p-3">
            <div style={{ height: "1050px", overflowY: 'auto' }}>
              <JobPage appNm={appNm} dmData={dmForTabs} />
            </div>
          </div>
        );

      case 'dataLineageCrossWalk':
        return (
          <div className="tab-content p-3">
            {/* Reuse the same top entity bar the businessEntity tab uses.
                When the CANONICAL source is active, navEntities are the
                canonical entities; selecting one drives the crosswalk below. */}
            <EntityNavBar
              entities={navEntities}
              selectedId={selectedEntityId || ''}
              onSelect={(eid) => selectEntity(appNm, eid)}
            />
            <div style={{ height: "1130px", overflowY: "auto" }}>
              
              <DataLineageCrosswalkView
                appNm={appNm}
                entityId={selectedEntityId}
                entityLabel={currentShell?.label}
                // optional: persist SME lineage edits to your backend
                onLineageSaved={(fldId, lineage) => {
                  // e.g. adriva_postAction(JSON.stringify({
                  //   action: 'UPDATE_LINEAGE', app_nm: appNm,
                  //   entity_id: selectedEntityId,
                  //   data: { canonical_fld_id: fldId, source_lineage: lineage },
                  // }), () => {}, () => {});
                  console.log('[lineage] save', fldId, lineage);
                }}
              />
              {/*This is for Canonical data model build lineage 
              <DataLineageCrosswalkView
                appNm={appNm}
                entityNm={currentShell?.name || currentShell?.label || 'Supplier'}  // ← "Supplier"
                entityLabel={currentShell?.label}
                onLineageSaved={(fldId, lineage) => {
                  console.log('[lineage] save', fldId, lineage);
                }}
              />
              */}
            </div>
          </div>
        );

      case 'canonicalRefData': {
        return (
          <div style={{ height: '1130px', overflowY: 'auto' }}>
            <CanonicalReferenceData
              canonicalEntityId={canonicalEntityId || 'CANONICAL'}
              appNm={appNm} />
          </div>
        );
      }

      default:
        return null;
    }
  }, [tabKey, selectedEntityId, currentShell, currentEntry, dmForTabs, navEntities, selectEntity,
      selectedDMEntityId, dm, data, dataSrcSystemId, srcSystems, tool, appNm,
      hierarchies, relationships, selectedApp, dgReviewActive, selectedDGEntityIds,
      fieldFilter, handleUpdateDMData, handleOpenDataModelTreeModal, handleSrcSystemValueChanges,
      handleRelAndHierarchySave, handleNavigateToField, refreshAppList]);

  // ─── source pill bar ───────────────────────────────────────────────────────

  const srcSystemCenter = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {dataSrcSystems.map((sys: any) => {
        const active = dataSrcSystemId === sys.srcSystemId;
        return (
          <span key={sys.srcSystemId}
            onClick={() => selectSource(sys.srcSystemId)}
            style={{ padding: '5px 12px', borderRadius: 12, fontSize: 12, fontWeight: 600,
              background: active ? T.brand : T.bgSidebar, color: active ? '#fff' : T.textMuted,
              border: `1px solid ${active ? T.brand : T.border}`, cursor: 'pointer', transition: 'all .15s' }}>
            {sys.srcSystemName}
          </span>
        );
      })}
    </div>
  );

  const headerActions = (
    <>
      <button className="gw-btn gw-btn-outline gw-btn-sm" onClick={() => setShowDataSrcSystemModal(true)}>
        + Add Data Source
      </button>
      <button className="gw-btn gw-btn-primary gw-btn-sm" onClick={handleSaveDMData}>
        Save
      </button>
      {tool === 'BUILD_MDM_USING_DATA' && (
        <MigrateDropdown onSelect={(t) => { setMigrationTool(t); handleMigrateDMData(); }} />
      )}
    </>
  );

  // ─── render: INITIAL_PAGE — landing ───────────────────────────────────────

  if (pageName === 'INITIAL_PAGE' && tool === '') {
    return <GenWizardLanding setTool={setTool} />;
  }

  // ─── render: INITIAL_PAGE — config selector ────────────────────────────────

  if (pageName === 'INITIAL_PAGE' && ['BUILD_MDM_USING_NLP','BUILD_MDM_USING_DATA',
      'BUILD_DQ_USING_DATA','BUILD_DG_USING_DATA',
      'MIGRATION_INFA_LEGACY_TO_IDMC','MIGRATION_INFA_LEGACY_TO_RELTIO'].includes(tool)) {
    return (
      <div style={{ backgroundColor: "#f4f7fc", height: "100vh", paddingTop: '5px' }}>
        <AppHeader tool={tool}
          onHome={() => { setPageName('INITIAL_PAGE'); setTabKey('businessEntity'); setToolState(''); setDataSrcSystemId(null); setSelectedDMEntity(null); setSelectedDMEntityId(null); }} />
        <CustomAlert show={message !== '' || error !== ''} message={message} error={error}
          bgBlock={bgBlock} alertWClose={alertWClose} onClose={clearAlert} />
        <ConfigSelectorPage tool={tool} toolAppList={toolAppList} onAppSelected={handleAppSelected}>
          {tool === 'BUILD_MDM_USING_NLP' && (
            <AriaAssistant appNm={appNm} tool={tool} useType={assistantUseType || undefined}
              handleUpdateMDMConfig={handleUpdateMDMConfig} handleUploadMDMConfig={handleUploadMDMConfig}
              handleStreamlitSetValue={handlePostData} />
          )}
          {['MIGRATION_INFA_LEGACY_TO_IDMC','MIGRATION_INFA_LEGACY_TO_RELTIO'].includes(tool) && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Application Name</Form.Label>
                <Form.Control type="text" value={mdmConfigAppSelectedVal}
                  onChange={(e) => setMdmConfigAppSelectedVal(e.target.value)} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Informatica ChangeList file:</Form.Label>
                <Form.Control type="file" accept=".xml"
                  onChange={(e) => { const f = e.target as HTMLInputElement; setInfaMDMChangeListFile(f.files?.[0] || null); }} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Informatica Operational metadata:</Form.Label>
                <Form.Control type="file" accept=".json"
                  onChange={(e) => { const f = e.target as HTMLInputElement; setInfaMDMDataStatFile(f.files?.[0] || null); }} />
              </Form.Group>
            </Form>
          )}
        </ConfigSelectorPage>
        {showDMERDiagramModal && (
          <Modal show={showDMERDiagramModal} onHide={() => setShowDMERDiagramModal(false)} fullscreen backdrop="static" keyboard={false}>
            <Modal.Header closeButton><Modal.Title>Data Model Design</Modal.Title></Modal.Header>
            <Modal.Body>
              <DMERDiagram data={rawMetaData} selectedFields={selectedDMFields}
                selectedDMPrimaryTable={selectedDMPrimaryTable} entity={selectedDMEntity}
                actionDMERDiagram={'BUILD_ENTITY'}
                handleUpdateMDMConfig={handleUpdateMDMConfig} handleUploadMDMConfig={handleUploadMDMConfig} />
            </Modal.Body>
          </Modal>
        )}
      </div>
    );
  }

  // ─── render: main app ──────────────────────────────────────────────────────

  return (
    <div style={{ backgroundColor: "#f4f7fc", minHeight: "100vh", paddingTop: '5px' }} className="component">
      <AppHeader appName={appNm} tool={tool}
        onHome={() => { setPageName('INITIAL_PAGE'); setToolState(''); setDataSrcSystemId(null); setSelectedDMEntity(null); setSelectedDMEntityId(null); }}
        center={dataSrcSystems?.length > 0 ? srcSystemCenter : undefined}
        actions={headerActions}
        onAdminOpen={() => (window as any).__adriva_openAdmin?.()} />

      <CustomAlert show={message !== '' || error !== ''} message={message} error={error}
        bgBlock={bgBlock} alertWClose={alertWClose} onClose={clearAlert} />

      <div style={{ display: 'flex', height: 'calc(100vh - 56px)' }}>
        <AppSidebar items={renderSidebarItems()} activeKey={tabKey}
          onSelect={(k) => { if (k) setTabKey(k); }}
          collapsed={sideBarCollapsed}
          onToggleCollapse={() => setSideBarCollapsed(p => !p)}
          appName={appNm} />
        <div style={{ flex: 1, overflow: 'auto', background: T.bg }}>
          {renderTabContent()}
        </div>
      </div>

      {showDataModelTreeModal && (
        <DataModelTreeModal show={showDataModelTreeModal}
          handleClose={() => setShowDataModelTreeModal(false)}
          handleSave={handleSaveDataModelTreeModalData}
          dataModelData={origData} selectedIds={selectedDataModelFieldIds} />
      )}

      <LoginModal tool={migrationTool || ''} show={showLoginModal} error={error} message={message}
        handleClose={() => setShowLoginModal(false)} handleLogin={handleLoginToSaaS} />

      <InfaMDMMigrationModal show={showInfaMDMMigrationModal} error={error} message={message}
        infaMDMSaaSLoggedIn={!!idmcSession} infaMDMSaaSProj={infaMDMSaaSProj}
        infaMDMSaaSFolder={infaMDMSaaSFolder} configs={infaMDMSaaSConfigMigration}
        handleClose={() => setShowInfaMDMMigrationModal(false)}
        handleTriggerInfaMDMSaaSMigration={handleTriggerInfaMDMSaaSMigration}
        pullInfaSaaSFolderList={pullInfaSaaSFolderList} />

      <ReltioMDMMigrationModal show={showReltioMDMMigrationModal} error={error} message={message}
        reltioMDMSaaSLoggedIn={!!reltioSession} configs={reltioMDMSaaSConfigMigration}
        handleClose={() => setShowReltioMDMMigrationModal(false)}
        handleTriggerReltioMDMSaaSMigration={handleTriggerReltioMDMSaaSMigration}
        setErrorOrMsg={(isErr: boolean, msg: string) => isErr ? showError(msg) : showSuccess(msg)} />

      {showDMERDiagramModal && (
        <Modal show={showDMERDiagramModal} onHide={() => setShowDMERDiagramModal(false)} fullscreen backdrop="static" keyboard={false}>
          <Modal.Header closeButton><Modal.Title>Entity Design</Modal.Title></Modal.Header>
          <Modal.Body>
            <DMERDiagram data={rawMetaData} selectedFields={selectedDMFields}
              selectedDMPrimaryTable={selectedDMPrimaryTable} entity={selectedDMEntity}
              actionDMERDiagram={'ADDR_UPDATE_ENTITY'}
              handleUpdateMDMConfig={handleUpdateMDMConfig} handleUploadMDMConfig={handleUploadMDMConfig} />
          </Modal.Body>
        </Modal>
      )}

      {showDataSrcSystemModal && (
        <Modal show={showDataSrcSystemModal} onHide={() => setShowDataSrcSystemModal(false)} size="xl" backdrop="static" keyboard={false}>
          <Modal.Header closeButton><Modal.Title>Add Data Source</Modal.Title></Modal.Header>
          <Modal.Body>
            <DataInputPage tool={tool as Tool} appNm={appNm} action={"ADD_DATA_SRC_EXIST_APP"} />
          </Modal.Body>
        </Modal>
      )}

      {showChatAssistant && (
        <Modal show={showChatAssistant} onHide={() => setShowChatAssistant(false)} size="xl" backdrop="static" keyboard={false}>
          <Modal.Header closeButton><Modal.Title>Aria - MDM Build Assistant</Modal.Title></Modal.Header>
          <Modal.Body>
            <AriaAssistant appNm={appNm} tool={tool} useType={assistantUseType || undefined}
              handleUpdateMDMConfig={handleUpdateMDMConfig} handleUploadMDMConfig={handleUploadMDMConfig}
              handleStreamlitSetValue={handlePostData} />
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
}